import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logger } from '@nykaa/logger';
import { standardErrorMessages } from '@nykaa/utils/logging/constants';
import { doAddToCart } from '@nykaa/product-card-shared/store/addToCart/action';
import { fetchCouponsAndOffers } from '@nykaa/coupon-shared/store/action';
import {
  addToWishlist,
  removeFromWishlist,
} from '@nykaa/product-card-shared/store/addToWishlist/action';
import {
  PRODUCT_ADDED_TO_WISHLIST,
  PRODUCT_REMOVED_FORM_WISHLIST,
  WISHLIST_SNACKBAR_ID,
} from '@nykaa/product-card-shared/constant/addToWishlist';
import {
  PRODUCT_ADDED_TO_CART,
  CART_SNACKBAR_ID,
} from '@nykaa/product-card-shared/constant/addToCart';
import { sendNotifyMe } from '@nykaa/product-card-shared/store/notifyMe/action';
import {
  AddToWishlist,
  RemoveFromWishlist,
} from '@nykaa/product-card-shared/types/addToWishlist';
import { CartActionParams } from '@nykaa/product-card-shared/types/addToCart';
import { NotifyMeParams } from '@nykaa/product-card-shared/types/notifyMe';
import {
  FAILURE_MESSAGE,
  NOTIFY_ME_SNACKBAR_ID,
  SUCCESS_MESSAGE,
} from '@nykaa/product-card-shared/constant/notifyMe';
import { removeFromWishlist as removeFromWishlistPage } from '@nykaa/wishlist-shared/store/action';

import {
  addToAuthWishlist,
  removeFromAuthWishlist,
  setAuthCartCount,
} from 'store/auth/action';
import { toggleSlider } from 'store/app/actions';
import { promiseWrapper, getSnackbarState } from 'helpers/product';
import Snackbar from 'components/Snackbar';
import { DEFAULT_SNACKBAR } from 'constants/product';
import { State } from 'store/types';
import { pushData, pushEvent } from '@nykaa/data-layer';
import { OPEN_NOTIFY_ME } from 'components/ProductList/constants';
import { ListingProduct } from '@nykaa/listing-shared/type/product';
import {
  generateProductAddToCartData,
  generateProductAddToWishlistData,
  generateProductNotifyMe,
  generateProductRemoveFromWishlistData,
  generateProductSelectSize,
  generateProductSelectShade,
} from '@nykaa/utils/retina/helpers';
import { sendSingleEvent } from 'helpers/impressions/sendSingleRetinaEvent';
import { isShade } from 'Nps/shared/helpers';
import { ProductTrackingData } from 'shared/helpers/impressions/types';
import {
  ADD_TO_CART_ERR_MESSAGE,
  deviceTypes,
  platformNames,
  UNAUTHORIZE_ERROR_CODE,
} from 'shared/constants';
import { getReactConfig } from 'shared/store/auth/service';
import actions from 'shared/store/auth/actionTypes';
import { useIsConvenienceFeeEnabled } from 'helpers/utils/hooks';
import { fetchProductOffers } from 'store/product/action';

interface Props {
  children: any;
  // It is required only to track click interaction from listing pages
  hasInteracted?: boolean;
  setInteraction?: React.Dispatch<React.SetStateAction<boolean>>;
  product?: ListingProduct;
  productTrackingData?: ProductTrackingData;
  isAnalyticsTrackingEnabled?: boolean;
  enableSecondaryTagTracking?: boolean;
}

const ActionWrapper = ({
  children,
  hasInteracted,
  setInteraction,
  productTrackingData,
  isAnalyticsTrackingEnabled,
  enableSecondaryTagTracking,
}: Props) => {
  const dispatch = useDispatch();
  const authData = useSelector((state: State) => state.auth);
  const { user } = authData;
  const { groupId, isLoyal = false } = user;
  const listingOpenedFrom = useSelector(
    (state: State) => state.dataLayer?.listingOpenedFrom || ''
  );
  const enableReactConfig = useSelector(
    (state: State) =>
      state?.remoteConfig?.switches?.enableReactConfigOnAddToCart
  );
  const isConvenienceFeeEnabled = useIsConvenienceFeeEnabled();
  const [snackBar, setSnackBar] = useState(DEFAULT_SNACKBAR);
  const sendNotifyMeAction = useCallback(
    async (params) => await dispatch(sendNotifyMe(params)),
    [dispatch]
  );
  const addToWishlistAction = useCallback(
    async (params) => await dispatch(addToWishlist(params)),
    [dispatch]
  );
  const removeFromWishlistAction = useCallback(
    async (params) => await dispatch(removeFromWishlist(params)),
    [dispatch]
  );

  const removeFromWishlistClick = useCallback(
    async (params) => await dispatch(removeFromWishlistPage(params.productId)),
    [dispatch]
  );

  const doAddToCartAction = useCallback(
    async (params: CartActionParams, convenienceChargesEnabled: boolean) => {
      try {
        return await dispatch(doAddToCart(params, convenienceChargesEnabled));
      } catch (error: any) {
        const statusCode = error?.response?.status;
        if (statusCode === UNAUTHORIZE_ERROR_CODE && enableReactConfig) {
          return getReactConfig().then((authData) => {
            dispatch({
              type: actions.FETCH_AUTH_DATA_SUCCESS,
              payload: authData,
            });
          });
        }
        logger.error(error, ADD_TO_CART_ERR_MESSAGE);
        throw error;
      }
    },
    [dispatch, enableReactConfig]
  );

  const isVariantSize = !isShade(
    productTrackingData?.product?.variantType || ''
  );
  // check if isAnalyticsTrackingEnabled flag is true and tracking data is defined
  const isAnalyticsEnabled = !!(
    isAnalyticsTrackingEnabled && productTrackingData
  );

  const handleSliderAction = useCallback(async () => {
    await dispatch(toggleSlider());
  }, [dispatch]);

  const wishlistCallback = useCallback(
    (isAdd: boolean, productId: string) => {
      if (isAdd) {
        dispatch(addToAuthWishlist(productId));
        return;
      }
      if (!isAdd) {
        dispatch(removeFromAuthWishlist(productId));
      }
    },
    [dispatch]
  );

  const addToBagCallback = useCallback(
    (data) => {
      dispatch(setAuthCartCount(data?.itemsQuantity));
    },
    [dispatch]
  );

  const handleInteraction = () => {
    if (setInteraction && !hasInteracted) {
      setInteraction(true);
      return {
        clickInteraction: listingOpenedFrom || '',
      };
    }
    return {};
  };

  const trackClickInteraction = () => {
    const clickInteraction = handleInteraction();
    pushData({ ...clickInteraction });
  };

  const handleVariantClick = () => {
    trackClickInteraction();
    isAnalyticsEnabled &&
      sendSingleEvent(
        isVariantSize
          ? generateProductSelectSize(productTrackingData)
          : generateProductSelectShade(productTrackingData)
      );
  };

  const openNotifyMe = () => {
    pushEvent(OPEN_NOTIFY_ME);
    trackClickInteraction();
    isAnalyticsEnabled &&
      sendSingleEvent(generateProductNotifyMe(productTrackingData));
  };

  const onRemoveFromWishlist = async (params: RemoveFromWishlist) => {
    removeFromWishlistClick(params);
    return { wishlistData: true };
  };

  const handleMoveToCart = async (
    params: CartActionParams,
    productId: string
  ) => {
    const [data, error] = await promiseWrapper(
      doAddToCartAction(params, isConvenienceFeeEnabled)
    );
    let msg = data
      ? PRODUCT_ADDED_TO_CART
      : standardErrorMessages.SOMETHING_WRONG;
    if (error?.errorInApi) {
      msg = error.message;
    }
    if (data) {
      removeFromWishlistClick({ productId });
      addToBagCallback(data);
    }
    setSnackBar(
      getSnackbarState(CART_SNACKBAR_ID, true, msg, Boolean(error), true)
    );
    return { wishlistData: data, error };
  };

  const handleWishlist = async (
    params: AddToWishlist | RemoveFromWishlist,
    isAdd: boolean
  ) => {
    const wishlistAction = isAdd
      ? addToWishlistAction
      : removeFromWishlistAction;
    const [data, error] = await promiseWrapper(wishlistAction(params));
    if (data) {
      wishlistCallback(isAdd, params.productId);
      trackClickInteraction();
    }
    let msg = isAdd ? PRODUCT_ADDED_TO_WISHLIST : PRODUCT_REMOVED_FORM_WISHLIST;
    if (error) {
      msg = standardErrorMessages.SOMETHING_WRONG;
    }
    setSnackBar(
      getSnackbarState(WISHLIST_SNACKBAR_ID, true, msg, Boolean(error))
    );
    !!(isAnalyticsTrackingEnabled && productTrackingData) && isAdd
      ? sendSingleEvent(generateProductAddToWishlistData(productTrackingData))
      : sendSingleEvent(
          generateProductRemoveFromWishlistData(productTrackingData)
        );
    return { wishlistData: data, error };
  };

  const handleAddToCart = async (params: CartActionParams) => {
    pushData({ enableSecondaryTagTracking });
    const [data, error] = await promiseWrapper(
      doAddToCartAction(params, isConvenienceFeeEnabled)
    );
    let msg = data
      ? PRODUCT_ADDED_TO_CART
      : standardErrorMessages.SOMETHING_WRONG;
    if (error && error.errorInApi) {
      msg = error.message;
    }
    if (data) {
      addToBagCallback(data);
      trackClickInteraction();
      const deviceType = deviceTypes.D_SITE;
      const domain = platformNames.NYKAA;
      const offerApiParams = {
        productId: params.productId,
        customerGroupId: parseInt(groupId) || 0,
        isLoyal,
        domain,
        deviceType,
        fetchUniversalOffers: true,
      };
      dispatch(fetchProductOffers(offerApiParams, true));
      dispatch(fetchCouponsAndOffers(offerApiParams, true));
    }

    setSnackBar(
      getSnackbarState(CART_SNACKBAR_ID, true, msg, Boolean(error), true)
    );
    isAnalyticsEnabled &&
      sendSingleEvent(generateProductAddToCartData(productTrackingData));
    return { cartData: data, error };
  };

  const handleNotifyMe = async (params: NotifyMeParams) => {
    const [data, error] = await promiseWrapper(sendNotifyMeAction(params));
    let msg = SUCCESS_MESSAGE;
    const isError = error || !data;
    if (isError) {
      msg = error || FAILURE_MESSAGE;
    }
    setSnackBar(getSnackbarState(NOTIFY_ME_SNACKBAR_ID, true, msg, isError));
    return { data, error };
  };

  const handleSnackBarClose = () => {
    setSnackBar({ ...DEFAULT_SNACKBAR });
  };
  const handleViewBag = () => {
    handleSliderAction();
  };
  const cartText = snackBar?.isCart ? ' View Bag' : '';
  if (!children) {
    return null;
  }

  return (
    <>
      {children({
        handleWishlist,
        handleAddToCart,
        handleNotifyMe,
        handleSnackBarClose,
        handleSliderAction,
        onRemoveFromWishlist,
        handleMoveToCart,
        handleVariantClick,
        openNotifyMe,
      })}
      {snackBar?.status && (
        <Snackbar
          autoClose
          text={cartText}
          content={snackBar?.msg}
          errorType={snackBar?.isError}
          snackBarId={snackBar?.id}
          closeCallback={handleSnackBarClose}
          handleTextClick={handleViewBag}
        />
      )}
    </>
  );
};

export default ActionWrapper;
